import { VendorSetupMedia, VendorSetupDetails, VendorSetupPricing, VendorSetupFeatures, VendorSetupSocialProof } from '@/contracts/vendor-setup';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, Star, Play, AlertCircle } from 'lucide-react';

interface Props {
  media: VendorSetupMedia;
  details: VendorSetupDetails;
  pricing: VendorSetupPricing;
  features: VendorSetupFeatures;
  socialProof: VendorSetupSocialProof;
}

export function SetupPreviewStep({ media, details, pricing, features, socialProof }: Props) {
  const issues: string[] = [];
  
  if (!details.title) issues.push('Product name is required');
  if (!details.description || details.description.length < 20) issues.push('Description must be at least 20 characters');
  if (details.categories.length === 0) issues.push('Select at least one category');
  if (!pricing.price) issues.push('Set a price for your product');
  if (!media.logo_url && !media.hero_image_url) issues.push('Add at least one image (logo or hero)');

  const savings = pricing.msrp && pricing.msrp > pricing.price 
    ? Math.round((1 - pricing.price / pricing.msrp) * 100) 
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Preview & Submit</h2>
        <p className="text-muted-foreground">
          Review how your listing will appear to agents. Make sure everything looks good before submitting.
        </p>
      </div>

      {/* Validation Issues */}
      {issues.length > 0 && (
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Please fix these issues before submitting
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
              {issues.map((issue, i) => (
                <li key={i}>{issue}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Preview Card */}
      <div className="border rounded-lg overflow-hidden">
        {/* Hero Section Preview */}
        <div className="relative bg-muted h-48 flex items-center justify-center">
          {media.hero_image_url ? (
            <img 
              src={media.hero_image_url} 
              alt="Hero" 
              className="w-full h-full object-cover"
            />
          ) : media.logo_url ? (
            <img 
              src={media.logo_url} 
              alt="Logo" 
              className="h-24 object-contain"
            />
          ) : (
            <span className="text-muted-foreground">No image uploaded</span>
          )}
          
          {savings && (
            <Badge className="absolute top-4 right-4 bg-green-600">
              Save {savings}%
            </Badge>
          )}
        </div>

        <div className="p-6 space-y-4">
          {/* Title & Categories */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              {media.logo_url && (
                <img src={media.logo_url} alt="" className="h-10 w-10 rounded-lg object-contain" />
              )}
              <div>
                <h3 className="text-2xl font-bold">{details.title || 'Product Name'}</h3>
                <p className="text-muted-foreground">{details.subtitle || 'Your tagline here'}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {details.categories.map((cat) => (
                <Badge key={cat} variant="secondary">{cat}</Badge>
              ))}
              {details.best_for.map((bf) => (
                <Badge key={bf} variant="outline">{bf}</Badge>
              ))}
            </div>
          </div>

          {/* Price Preview */}
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold">${pricing.price}</span>
            {pricing.msrp && pricing.msrp > pricing.price && (
              <span className="text-lg text-muted-foreground line-through">${pricing.msrp}</span>
            )}
            <span className="text-muted-foreground">/{pricing.price_duration}</span>
          </div>

          {/* Description */}
          <p className="text-muted-foreground">
            {details.description || 'Your product description will appear here...'}
          </p>

          {/* Features Preview */}
          {features.features.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold">Key Features</h4>
              <ul className="grid grid-cols-2 gap-2">
                {features.features.slice(0, 6).map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-green-600" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Plans Preview */}
          {pricing.plans.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold">Pricing Plans</h4>
              <div className="grid grid-cols-3 gap-3">
                {pricing.plans.map((plan, i) => (
                  <Card key={i}>
                    <CardContent className="p-4 text-center">
                      <p className="font-medium">{plan.name}</p>
                      <p className="text-xl font-bold">${plan.price}</p>
                      <p className="text-xs text-muted-foreground">
                        {plan.features.length} features
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Testimonials Preview */}
          {socialProof.testimonial_videos.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold">Testimonials</h4>
              <div className="flex gap-3">
                {socialProof.testimonial_videos.slice(0, 3).map((video, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm bg-muted rounded-lg p-2">
                    <Play className="h-4 w-4" />
                    <span>{video.title || `Video ${i + 1}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submit Info */}
      <Card className="bg-muted/50">
        <CardContent className="pt-4">
          <h4 className="font-semibold mb-2">What happens next?</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              Your listing will be reviewed by our team within 2-3 business days
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              We may reach out with questions or suggestions
            </li>
            <li className="flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              Once approved, your product will go live in the marketplace
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
